import React, { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

function pgList(page, pages) {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  if (page <= 4) return [1, 2, 3, 4, 5, "…", pages];
  if (page >= pages - 3) return [1, "…", pages - 4, pages - 3, pages - 2, pages - 1, pages];
  return [1, "…", page - 1, page, page + 1, "…", pages];
}

const ChevsLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="11 17 6 12 11 7" /><polyline points="18 17 13 12 18 7" />
  </svg>
);
const ChevLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const ChevsRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="13 17 18 12 13 7" /><polyline points="6 17 11 12 6 7" />
  </svg>
);

function TablePagination({
  paginationData,
  isSmallScreen,
  paginatedDataLength,
  totalRecords,
  onPageChange,
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = Number.parseInt(searchParams.get("page")) || 1;
  const pageSize = Number.parseInt(searchParams.get("pageSize")) || 10;

  const total = paginationData?.total ?? totalRecords ?? 0;
  const backendPageSize = paginationData?.pageSize || paginationData?.limit || pageSize;
  const totalPages = Math.max(
    1,
    paginationData?.totalPages ?? (total > 0 ? Math.ceil(total / backendPageSize) : 1)
  );

  const hasPrev = paginationData?.hasPrev ?? (currentPage > 1);
  const hasNext =
    paginationData?.hasNext !== undefined
      ? Boolean(paginationData.hasNext)
      : currentPage < totalPages && totalPages > 1;

  const handlePageChange = useCallback(
    (newPage) => {
      const targetPage = Math.max(1, Math.min(newPage, totalPages));
      if (targetPage === currentPage) return;
      const params = new URLSearchParams(searchParams);
      params.set("page", String(targetPage));
      params.set("pageSize", String(pageSize));
      setSearchParams(params, { replace: true });
      onPageChange?.(targetPage, pageSize);
    },
    [searchParams, setSearchParams, pageSize, totalPages, currentPage, onPageChange]
  );

  const handleRowsPerPageChange = useCallback(
    (e) => {
      const newPageSize = Number(e.target.value);
      const params = new URLSearchParams(searchParams);
      params.set("pageSize", String(newPageSize));
      params.set("page", "1");
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const nums = pgList(currentPage, Math.max(totalPages, 1));

  if (isSmallScreen) {
    return (
      <div className="ems-pager ems-pager-mobile">
        <span className="ems-pg-info">
          <b>{paginatedDataLength || 0}</b> of <b>{total}</b>
        </span>
        <div className="ems-pg-nav">
          <button
            className="ems-pg-btn"
            disabled={!hasPrev}
            onClick={() => handlePageChange(currentPage - 1)}
            aria-label="Previous page"
          >
            <ChevLeft />
          </button>
          <span className="ems-pg-page">
            <b>{currentPage}</b> / <b>{totalPages}</b>
          </span>
          <button
            className="ems-pg-btn"
            disabled={!hasNext}
            onClick={() => handlePageChange(currentPage + 1)}
            aria-label="Next page"
          >
            <ChevRight />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ems-pager">
      <span className="ems-pg-info">
        Showing <b>{paginatedDataLength || 0}</b> of <b>{total}</b> records
      </span>
      <div className="ems-pg-spacer" />
      <div className="ems-rpp">
        Rows per page
        <select
          className="ems-toolbar-select"
          value={pageSize}
          onChange={handleRowsPerPageChange}
          style={{ minWidth: 72 }}
        >
          {[10, 25, 50, 100]
            .concat(pageSize)
            .filter((v, i, arr) => arr.indexOf(v) === i)
            .sort((a, b) => a - b)
            .map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
        </select>
      </div>
      <span className="ems-pg-page">
        Page <b>{currentPage}</b> of <b>{totalPages}</b>
      </span>
      <div className="ems-pg-nav">
        <button
          className="ems-pg-btn"
          disabled={!hasPrev || currentPage <= 1}
          onClick={() => handlePageChange(1)}
          aria-label="First page"
        >
          <ChevsLeft />
        </button>
        <button
          className="ems-pg-btn"
          disabled={!hasPrev}
          onClick={() => handlePageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          <ChevLeft />
        </button>
        {nums.map((n, i) =>
          n === "…" ? (
            <span key={"e" + i} className="ems-pg-ellipsis">…</span>
          ) : (
            <button
              key={n}
              className={"ems-pg-num" + (n === currentPage ? " active" : "")}
              onClick={() => handlePageChange(n)}
            >
              {n}
            </button>
          )
        )}
        <button
          className="ems-pg-btn"
          disabled={!hasNext}
          onClick={() => handlePageChange(currentPage + 1)}
          aria-label="Next page"
        >
          <ChevRight />
        </button>
        <button
          className="ems-pg-btn"
          disabled={!hasNext || currentPage >= totalPages}
          onClick={() => handlePageChange(totalPages)}
          aria-label="Last page"
        >
          <ChevsRight />
        </button>
      </div>
    </div>
  );
}

export default TablePagination;
